const PropTypes = require('prop-types');
const React = require('react');
const classNames = require('classnames');
const traits = require('./traits.js');
const DropdownVariant = require('./dropdown_variant.jsx');
const User = require('components/user.js');
const { OperatingSystem } = require('@reverbdotcom/discovery-ui');
const OperatingSystemIcons = require('./operating_system_icons.js');
const { UserStore } = require('@reverbdotcom/discovery-ui');

class DownloadButton extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedVariant: this.getDefaultVariant(),
      isOpen: false,
    };

    this.handleContainerClick = this.handleContainerClick.bind(this);
    this.handleDownloadClick = this.handleDownloadClick.bind(this);
    this.toggleDropdown = this.toggleDropdown.bind(this);
  }

  componentDidMount() {
    document.addEventListener('click', () => this.handleDocumentClick());
  }

  componentWillUnmount() {
    document.removeEventListener('click', () => this.handleDocumentClick());
  }

  get downloadLink() {
    if (this.props.gt) {
      return `${this.state.selectedVariant.fileUrl}?gt=${this.props.gt}`;
    }
    return this.state.selectedVariant.fileUrl;
  }

  getDefaultVariant() {
    return this.variantMatchingOperatingSystem() || this.props.variants[0];
  }

  handleDocumentClick() {
    this.setState({ isOpen: false });
  }

  handleContainerClick(e) {
    if (e.nativeEvent.stopImmediatePropagation) {
      // This does not currently work with react test-utils simulated clicks
      e.nativeEvent.stopImmediatePropagation();
    }
  }

  handleChooseVariant(variant) {
    this.setState({ selectedVariant: variant, isOpen: false });
  }

  handleDownloadClick(e) {
    e.preventDefault();
    if (UserStore.getState().loggedOut) {
      User.showInlineRegistrationOrLogin(e, e.target);
    } else {
      this.props.downloadClicked(this.downloadLink);
    }
  }

  toggleDropdown() {
    this.setState({ isOpen: !this.state.isOpen });
  }

  variantMatchingOperatingSystem() {
    if (OperatingSystem.isWindows()) {
      return this.variantForOperatingSystem(traits.operatingSystem.windows);
    } else if (OperatingSystem.isMac()) {
      return this.variantForOperatingSystem(traits.operatingSystem.osx);
    } else if (OperatingSystem.isLinux()) {
      return this.variantForOperatingSystem(traits.operatingSystem.linux);
    }
    return false;
  }

  variantForOperatingSystem(operatingSystem) {
    return this.props.variants.find((variant) =>
      variant.operatingSystem.value_uuid === operatingSystem.uuid
    );
  }

  downloadUnavailable() {
    return !this.state.selectedVariant;
  }

  get downloadAction() {
    return this.state.selectedVariant.downloadText || I18n.t('digitalListing.downloadAction');
  }

  // Digital listings can have either one variant with multiple operating systems,
  // OR multiple variants with one operating system each
  // If we have multiple operating systems we want to hide the OS selection dropdown
  // and remove other OS specific items
  get hasUniversalOperatingSystemSupport() {
    const multipleOSVariant = this.props.variants.find(variant =>
      variant.operatingSystemTraits.length > 1
    );
    return !!multipleOSVariant;
  }

  get downloadedButtonClassNames() {
    return classNames(
      'btn',
      'btn-primary',
      'download-btn',
      'btn-disabled',
      'wide', {
        'download-btn--wide': this.hasUniversalOperatingSystemSupport,
      }
    );
  }

  get downloadButtonClassNames() {
    return classNames(
      'btn',
      'btn-primary',
      'download-btn', {
        'download-btn--wide': this.hasUniversalOperatingSystemSupport,
      }
    );
  }

  downloadedButton() {
    return (
      <div className="digital-download-actions">
        <div className="digital-download-actions__btn-group">
          <div className={this.downloadedButtonClassNames}>
            {this.renderOperatingSystemIcon()}
            {this.downloadAction}
          </div>
          {this.renderDisabledDropdownButton()}
        </div>
      </div>
    );
  }

  renderDropdownOptions() {
    return this.props.variants.map((variant, index) =>
      <DropdownVariant
        variant={variant}
        key={index}
        onChooseVariant={(chosen) => this.handleChooseVariant(chosen)}
      />
    );
  }

  renderDownloadLink() {
    return (
      <a
        href={this.downloadLink}
        className={this.downloadButtonClassNames}
        onClick={this.handleDownloadClick}
        ref="downloadBtn"
        data-registration-message={I18n.t('digitalListing.registrationWallMessage')}
        data-registration-url-source="sync"
      >
        {this.renderOperatingSystemIcon()}
        {this.downloadAction}
      </a>
    );
  }

  renderOperatingSystemIcon() {
    if (this.hasUniversalOperatingSystemSupport) { return null; }
    if (this.state.selectedVariant.downloadText) { return null; }

    return (
      <i
        className={classNames(
          'fa',
          'mr-half',
          `fa-${OperatingSystemIcons[this.state.selectedVariant.operatingSystem.value_uuid]}`
        )}
      />
    );
  }

  renderDisabledDropdownButton() {
    if (this.hasUniversalOperatingSystemSupport) {
      return null;
    }

    return (
      <button className="btn btn-primary toggle-option-btn btn-disabled">
        <i className="fa fa-caret-down" />
      </button>
    );
  }

  renderDropdownButton() {
    if (this.hasUniversalOperatingSystemSupport) {
      return null;
    }

    return (
      <button
        className="btn btn-primary toggle-option-btn"
        onClick={this.toggleDropdown}
      >
        <i className="fa fa-caret-down"></i>
      </button>
    );
  }

  renderDropdownContent() {
    if (this.hasUniversalOperatingSystemSupport) {
      return null;
    }

    return (
      <div className="dropdown shadowed bd-rounded pointer-right pointer-top">
        <div className="strong fine-print mb-half">
          {I18n.t('digitalListing.supportedFormats')}
        </div>
        {this.renderDropdownOptions()}
      </div>
    );
  }

  render() {
    if (this.downloadUnavailable()) {
      return (
        <div className="btn btn-primary download-btn btn-disabled wide">
          {I18n.t('digitalListing.noFilesFound')}
        </div>
      );
    }

    if (this.props.downloaded) {
      return this.downloadedButton();
    }

    return (
      <div className={classNames('digital-download-actions', { open: this.state.isOpen })}>
        <div
          className="digital-download-actions__btn-group"
          onClick={this.handleContainerClick}
        >
          {this.renderDownloadLink()}
          {this.renderDropdownButton()}
        </div>
        {this.renderDropdownContent()}
      </div>
    );
  }
}

DownloadButton.propTypes = {
  variants: PropTypes.array.isRequired,
  downloadClicked: PropTypes.func.isRequired,
  gt: PropTypes.string,
  downloaded: PropTypes.bool,
};


module.exports = DownloadButton;



// WEBPACK FOOTER //
// ./javascripts/components/digital_listings/download_button.jsx